import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import React, { createContext, useCallback, useContext, useMemo } from 'react'

export interface SearchResultsNavigationContextType {
  resultDocuments: DocumentTableRow[]
  currentIndex: number
  totalCount: number
  canGoNext: boolean
  canGoPrevious: boolean
  goToNext: () => void
  goToPrevious: () => void
  goToIndex: (index: number) => void
}

const SearchResultsNavigationContext = createContext<
  SearchResultsNavigationContextType | undefined
>(undefined)

interface SearchResultsNavigationProviderProps {
  children: React.ReactNode
  resultDocuments: DocumentTableRow[]
  currentDocumentId: string
  onNavigateToDocument: (documentId: string) => void
}

export const SearchResultsNavigationProvider: React.FC<
  SearchResultsNavigationProviderProps
> = ({
  children,
  resultDocuments,
  currentDocumentId,
  onNavigateToDocument,
}) => {
  const currentIndex = useMemo(
    () => resultDocuments.findIndex((doc) => doc.id === currentDocumentId),
    [resultDocuments, currentDocumentId]
  )

  const totalCount = resultDocuments.length
  const canGoNext = currentIndex >= 0 && currentIndex < totalCount - 1
  const canGoPrevious = currentIndex > 0

  const goToNext = useCallback(() => {
    if (canGoNext) {
      onNavigateToDocument(resultDocuments[currentIndex + 1].id)
    }
  }, [canGoNext, resultDocuments, currentIndex, onNavigateToDocument])

  const goToPrevious = useCallback(() => {
    if (canGoPrevious) {
      onNavigateToDocument(resultDocuments[currentIndex - 1].id)
    }
  }, [canGoPrevious, resultDocuments, currentIndex, onNavigateToDocument])

  const goToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalCount) {
        onNavigateToDocument(resultDocuments[index].id)
      }
    },
    [resultDocuments, totalCount, onNavigateToDocument]
  )

  const value = useMemo<SearchResultsNavigationContextType>(
    () => ({
      resultDocuments,
      currentIndex,
      totalCount,
      canGoNext,
      canGoPrevious,
      goToNext,
      goToPrevious,
      goToIndex,
    }),
    [
      resultDocuments,
      currentIndex,
      totalCount,
      canGoNext,
      canGoPrevious,
      goToNext,
      goToPrevious,
      goToIndex,
    ]
  )

  return (
    <SearchResultsNavigationContext.Provider value={value}>
      {children}
    </SearchResultsNavigationContext.Provider>
  )
}

export const useSearchResultsNavigation = ():
  | SearchResultsNavigationContextType
  | undefined => {
  return useContext(SearchResultsNavigationContext)
}
